import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, CreditCard, Package, ArrowRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

export default function Pricing() {
    useEffect(() => {
        document.title = "Pricing | Computer Repairs Semaphore";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Simple, upfront pricing for onsite computer repairs in Semaphore SA. $139 first hour, $60 each additional 30 minutes. No hidden rates."
            );
        }
        window.scrollTo(0, 0);
    }, []);

    return (
        <main data-testid="pricing-page">
            <section className="bg-slate-50 border-b border-slate-100">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-16">
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#0F2E52] leading-tight mb-3">
                        Simple, Upfront Pricing
                    </h1>
                    <p className="font-heading text-lg md:text-xl font-semibold text-blue-600 mb-4">
                        Know what you'll pay before you book.
                    </p>
                    <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-2xl">
                        Our standard on-site service pricing is simple and clearly explained before you make an
                        appointment.
                    </p>
                </div>
            </section>

            <section data-testid="pricing-rates" className="bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                    <div className="grid md:grid-cols-2 gap-5 mb-6">
                        <div
                            data-testid="price-first-hour"
                            className="bg-white rounded-2xl border-2 border-[#ea580c] shadow-md p-6 sm:p-8"
                        >
                            <p className="font-heading text-5xl sm:text-6xl font-bold text-[#0F2E52] mb-1">
                                $139
                            </p>
                            <p className="font-heading text-xl font-semibold text-[#ea580c] mb-3">First Hour</p>
                            <p className="text-base text-slate-700 leading-relaxed">
                                Includes up to the first 60 minutes of service.
                            </p>
                        </div>
                        <div
                            data-testid="price-additional-time"
                            className="bg-white rounded-2xl border-2 border-[#0F2E52] shadow-md p-6 sm:p-8"
                        >
                            <p className="font-heading text-5xl sm:text-6xl font-bold text-[#0F2E52] mb-1">$60</p>
                            <p className="font-heading text-xl font-semibold text-blue-600 mb-3">
                                Each Additional 30 Minutes
                            </p>
                            <p className="text-base text-slate-700 leading-relaxed">
                                After the first hour, additional time is charged at $60 for each additional 30
                                minutes.
                            </p>
                        </div>
                    </div>
                    <div className="max-w-3xl space-y-3 text-base leading-relaxed text-slate-700">
                        <p>
                            If additional time is required, the technician will discuss this with you before
                            continuing.
                        </p>
                        <p className="font-medium text-[#0F2E52]">
                            There are no hidden hourly rates and you don't need to contact us simply to find out
                            our standard service pricing.
                        </p>
                    </div>
                </div>
            </section>

            <section data-testid="pricing-hardware" className="bg-[#eff6ff]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-blue-600 shadow-sm">
                            <Package className="w-5 h-5" strokeWidth={1.5} />
                        </span>
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F2E52]">
                            Hardware &amp; Software
                        </h2>
                    </div>
                    <div className="max-w-3xl space-y-3 text-base leading-relaxed text-slate-700">
                        <p>The prices above cover the technician's service and labour.</p>
                        <p>
                            If any hardware, parts, software or licences are required to complete the work, the
                            technician will discuss this with you during the visit, including any additional costs,
                            before anything is purchased or supplied.
                        </p>
                        <p className="font-medium text-[#0F2E52]">
                            No additional hardware or software costs will be added without discussing them with you
                            first.
                        </p>
                    </div>
                </div>
            </section>

            <section data-testid="pricing-payment" className="bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                    <div className="bg-[#0F2E52] rounded-2xl p-6 sm:p-8 max-w-3xl">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-orange-400">
                                <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                            </span>
                            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                                Payment on Completion
                            </h2>
                        </div>
                        <ul className="space-y-2.5 text-base leading-relaxed text-blue-100">
                            <li>
                                Easy payments onsite — EFTPOS or cash accepted once the job is completed.
                            </li>
                            <li>Payment is due on completion of the work, at the end of the appointment.</li>
                            <li>Please have a valid credit or debit card, or cash, available for payment.</li>
                            <li>A receipt will be provided following payment.</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section data-testid="pricing-cta" className="bg-slate-50 border-t border-slate-100">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F2E52] mb-2">
                        Ready to book?
                    </h2>
                    <p className="text-base text-slate-700 mb-6">
                        Call now, or send us a message and we'll arrange a time to come to you.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <a
                            href={PHONE_TEL}
                            data-testid="pricing-call-now-button"
                            className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white font-semibold rounded-full px-8 py-3 transition-colors shadow-lg shadow-orange-500/20"
                        >
                            <Phone className="w-4 h-4" />
                            Call Now — {PHONE_DISPLAY}
                        </a>
                        <Link
                            to="/#contact"
                            data-testid="pricing-contact-link"
                            className="inline-flex items-center gap-1.5 bg-white border-2 border-[#0F2E52] text-[#0F2E52] hover:bg-slate-50 font-semibold rounded-full px-8 py-3 transition-colors"
                        >
                            Contact Us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
